import PropTypes from 'prop-types';
import React from 'react';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Links from '~/components/Layout/Sidebar/Links';
import { getActiveOrganization } from '~/utils/organization';
import { isAdminOfSomeOrganization } from '~/utils/validations/profile';
import { getLinks } from '~/utils/navigation';
import { getPath } from '~/utils/routes';
import SavedSearches from './SavedSearches';
import styles from './NavigationSidebar.css';
import PublishButton from '../Home/PublishButton';
import ProductHeading from '../ProductHeading';

export default class NavigationSidebar extends React.PureComponent {
  static propTypes = {
    isInitialized: PropTypes.bool,
    isStudio: PropTypes.bool,
    isPublicPortalEnabled: PropTypes.bool,
    isReuseMetricsEnabled: PropTypes.bool,
    isAgentFabricGAEnabled: PropTypes.bool,
    isAgentScannersEnabled: PropTypes.bool,
    isANGSearchEnabled: PropTypes.bool,
    isANGSearchOptInVisible: PropTypes.bool,
    isANGSearchOptOutVisible: PropTypes.bool,
    onShowANGSearchOptIn: PropTypes.func,
    onShowANGSearchOptOut: PropTypes.func,
    onActivateANGSearch: PropTypes.func,
    onDeactivateANGSearch: PropTypes.func,
    profile: PropTypes.object,
    location: PropTypes.object,
    presetLinkToEnabledMap: PropTypes.object,
    isOrgLevelSettingsEnabled: PropTypes.bool
  };

  static defaultProps = {
    isStudio: false,
    isPublicPortalEnabled: true,
    isANGSearchEnabled: false,
    isAgentFabricGAEnabled: false,
    isAgentScannersEnabled: false,
    isReuseMetricsEnabled: false,
    isANGSearchOptInVisible: false,
    onShowANGSearchOptIn: () => {},
    onShowANGSearchOptOut: () => {},
    onActivateANGSearch: () => {},
    presetLinkToEnabledMap: {}
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.isInitialized && this.props.isInitialized) {
      const {
        isANGSearchOptInVisible,
        isANGSearchOptOutVisible,
        onShowANGSearchOptIn,
        onShowANGSearchOptOut
      } = this.props;
      const isHomePage = this.isHomePage();
      const showANGSearchOptIn = isHomePage && isANGSearchOptInVisible;
      const showANGSearchOptOut = isHomePage && isANGSearchOptOutVisible;

      if (showANGSearchOptIn) {
        onShowANGSearchOptIn();
      }

      if (showANGSearchOptOut) {
        onShowANGSearchOptOut();
      }
    }
  }

  getNavigationLinks = () => {
    const {
      profile,
      isStudio,
      isPublicPortalEnabled,
      isReuseMetricsEnabled,
      isAgentScannersEnabled
    } = this.props;

    if (!isStudio && profile) {
      const navigationLinks = [];
      const organization = profile.organization || {};
      const isExchangeAdmin = organization?.isExchangeAdmin ?? false;
      const isEngagementEnabled =
        organization?.entitlements?.exchange2?.assetUsageAndEngagement
          ?.enabled ?? false;

      if (isReuseMetricsEnabled && isAdminOfSomeOrganization(profile)) {
        navigationLinks.push({
          name: 'Asset engagement',
          to: getPath('metrics')
        });
      }

      navigationLinks.push({
        name: 'My applications',
        to: getPath('applications')
      });

      if (isAgentScannersEnabled && isExchangeAdmin && isEngagementEnabled) {
        navigationLinks.push({
          name: 'Agent scanners',
          to: getPath('agentScanners')
        });
      }

      if (isPublicPortalEnabled && isExchangeAdmin) {
        navigationLinks.push({
          name: 'Public portal',
          href: getPath('publicPortal', { organization }),
          target: '_blank'
        });
      }

      if (isExchangeAdmin) {
        const id = this.props.isOrgLevelSettingsEnabled
          ? 'settings'
          : undefined;

        navigationLinks.push({
          name: 'Settings',
          to: getPath('settings'),
          id
        });
      }

      return navigationLinks;
    }

    return [];
  };

  isHomePage = () => {
    const { location } = this.props;

    return getPath('home') === location.pathname;
  };

  render() {
    const {
      isANGSearchEnabled,
      isAgentFabricGAEnabled,
      isANGSearchOptInVisible,
      isANGSearchOptOutVisible,
      presetLinkToEnabledMap,
      profile,
      location,
      onActivateANGSearch,
      onDeactivateANGSearch
    } = this.props;
    const links = getLinks(
      profile,
      location,
      isAgentFabricGAEnabled,
      isANGSearchEnabled,
      presetLinkToEnabledMap
    );
    const navigationLinks = this.getNavigationLinks();
    const activeOrganization = getActiveOrganization(profile);
    const isHomePage = this.isHomePage();

    return (
      <nav data-test-id="navigation-sidebar" aria-label="Home">
        <ProductHeading isSidebarItem />
        <PublishButton />
        <Links id="asset-filters" items={links} testId="home-links" />
        <SavedSearches
          display-if={isHomePage}
          id="organization-saved-searches"
          testId="home-bg-saved-searches"
          title={`${activeOrganization?.name} searches`}
          type="organization"
        />
        <SavedSearches
          display-if={isHomePage}
          id="user-saved-searches"
          testId="home-user-saved-searches"
          title="My searches"
          type="user"
        />
        <div id="other-links" display-if={navigationLinks.length > 0}>
          <hr display-if={isHomePage} />
          <Links items={navigationLinks} testId="navigation-links" />
        </div>
        <Button
          className={styles.newSearch}
          testId="activate-ang-search"
          title="Return to enhanced search"
          display-if={isHomePage && isANGSearchOptInVisible}
          kind="secondary"
          noFill
          onClick={onActivateANGSearch}
        >
          Return to enhanced search
        </Button>
        <Button
          className={styles.newSearch}
          testId="deactivate-ang-search"
          title="Back to legacy search"
          display-if={isHomePage && isANGSearchOptOutVisible}
          kind="secondary"
          noFill
          onClick={onDeactivateANGSearch}
        >
          Back to legacy search
        </Button>
      </nav>
    );
  }
}
